import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../services/api';
import { useAppSelector } from '../store/hooks';
import { selectNairaPerYuan, toDisplayNaira, formatNaira } from '../store/slices/currencySlice';
import { getStorageUrl } from '../services/api';
import './OrderDetail.css';

const statusLabels = {
  pending: 'Pending',
  processing: 'Processing',
  purchased: 'Purchased',
  shipped_out: 'Shipped Out',
  in_nigeria: 'In Nigeria',
  completed: 'Completed',
  refund: 'Refund',
  cancelled: 'Cancelled',
};

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [receiptFile, setReceiptFile] = useState(null);
  const [receiptDescription, setReceiptDescription] = useState('');
  const [receiptUploading, setReceiptUploading] = useState(false);
  const [receiptUploadError, setReceiptUploadError] = useState('');
  const nairaPerYuan = useAppSelector(selectNairaPerYuan);

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      try {
        setLoading(true);
        setError('');
        const response = await api.get(`/orders/${id}`);
        setOrder(response.data);
      } catch (err) {
        console.error('Failed to fetch order', err);
        setError(err.response?.status === 403 ? 'You do not have access to this order.' : err.response?.data?.message || 'Failed to load order.');
        setOrder(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <div className="order-detail-page"><p className="order-detail-loading">Loading order...</p></div>
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout>
        <div className="order-detail-page">
          <p className="order-detail-error">{error || 'Order not found.'}</p>
          <Link to="/my-orders">Back to My Orders</Link>
        </div>
      </Layout>
    );
  }

  const normalizeImagePath = (path) => {
    if (!path) return '';
    const p = String(path);
    if (p.startsWith('http://') || p.startsWith('https://')) {
      try {
        const url = new URL(p);
        return url.pathname || p;
      } catch {
        return p;
      }
    }
    return p;
  };

  const handleReceiptUpload = async (e) => {
    e.preventDefault();
    if (!receiptFile || !id) return;
    setReceiptUploadError('');
    setReceiptUploading(true);
    try {
      const formData = new FormData();
      formData.append('receipt', receiptFile);
      if (receiptDescription.trim()) formData.append('description', receiptDescription.trim());
      await api.post(`/orders/${id}/receipt`, formData);
      const response = await api.get(`/orders/${id}`);
      setOrder(response.data);
      setReceiptFile(null);
      setReceiptDescription('');
    } catch (err) {
      setReceiptUploadError(err.response?.data?.message || 'Failed to upload receipt.');
    } finally {
      setReceiptUploading(false);
    }
  };

  return (
    <Layout>
      <div className="order-detail-page">
        <div className="order-detail-container">
          <Link to="/my-orders" className="order-detail-back">← My Orders</Link>
          <h1>Order {order.order_number}</h1>
          <div className="order-detail-status">
            <span className={`status-badge status-${order.status}`}>
              {statusLabels[order.status] || order.status}
            </span>
            <span className="order-detail-date">Placed {new Date(order.created_at).toLocaleString()}</span>
          </div>

          <section className="order-detail-section">
            <h2>Items</h2>
            <ul className="order-detail-items">
              {order.items?.map((item) => {
                const rawImg = item.product?.image ?? item.product_snapshot?.product?.image ?? item.product_image;
                const imgPath = normalizeImagePath(rawImg);
                return (
                  <li key={item.id} className="order-detail-item">
                    {imgPath && (
                      <img src={getStorageUrl(imgPath)} alt={item.product_name} className="order-detail-item-img" />
                    )}
                    <div className="order-detail-item-info">
                      <span className="order-detail-item-name">{item.product_name}</span>
                      {item.selected_size && <span className="order-detail-item-size">{item.selected_size}</span>}
                      <span className="order-detail-item-qty">Qty: {item.quantity}</span>
                    </div>
                    <span className="order-detail-item-subtotal">
                      {formatNaira(toDisplayNaira(item.subtotal, nairaPerYuan))}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>

          {(order.shipping_name || order.shipping_address) && (
            <section className="order-detail-section">
              <h2>Shipping</h2>
              {order.shipping_name && <p>{order.shipping_name}</p>}
              {order.shipping_phone && <p>{order.shipping_phone}</p>}
              {(order.shipping_address || order.shipping_city) && (
                <p>{[order.shipping_address, order.shipping_city, order.shipping_state].filter(Boolean).join(', ')}</p>
              )}
            </section>
          )}

          <section className="order-detail-totals">
            <div className="order-detail-row">
              <span>Subtotal</span>
              <span>{formatNaira(toDisplayNaira(order.subtotal, nairaPerYuan))}</span>
            </div>
            <div className="order-detail-row order-detail-total">
              <span>Total</span>
              <span>{formatNaira(toDisplayNaira(order.total, nairaPerYuan))}</span>
            </div>
          </section>

          <section className="order-detail-section order-detail-receipt-section">
            <h2>Payment receipt</h2>
            {order.payment_receipt ? (
              <div className="order-detail-receipt-existing">
                {order.payment_receipt_note && <p className="order-detail-receipt-note">{order.payment_receipt_note}</p>}
                <img src={getStorageUrl(order.payment_receipt)} alt="Payment receipt" className="order-detail-receipt-img" />
              </div>
            ) : null}
            <p className="order-detail-receipt-hint">You can upload your payment receipt here if you did not upload it at checkout.</p>
            <form onSubmit={handleReceiptUpload} className="order-detail-receipt-form">
              <div className="order-detail-receipt-field">
                <label htmlFor="receipt-desc">Description (optional)</label>
                <input
                  id="receipt-desc"
                  type="text"
                  placeholder="e.g. Transfer ref, date of payment"
                  value={receiptDescription}
                  onChange={(e) => setReceiptDescription(e.target.value)}
                  className="order-detail-receipt-input"
                />
              </div>
              <div className="order-detail-receipt-field">
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                  onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                />
              </div>
              <button type="submit" className="order-detail-receipt-btn" disabled={!receiptFile || receiptUploading}>
                {receiptUploading ? 'Uploading...' : 'Upload receipt'}
              </button>
            </form>
            {receiptUploadError && <p className="order-detail-error">{receiptUploadError}</p>}
          </section>
        </div>
      </div>
    </Layout>
  );
};

export default OrderDetail;
